import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/**
 * Mobile app views and their saved version snapshots — mirrors stacks-agents
 * `src/app/api/views/`. Versions live here rather than in their own service
 * because a version *is* a snapshot of the view set.
 */
@Injectable({ providedIn: 'root' })
export class ViewsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getProjectViews(projectId: string): Promise<Record<string, any>> {
    return this.http
      .get<{ data?: Record<string, any> }>(`${environment.agentsUrl}v4/retrieval/views`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, any>>
  }

  getVersions(projectId: string): Promise<Record<string, boolean>> {
    return this.http
      .get<{ data?: Record<string, boolean> }>(`${environment.agentsUrl}v4/retrieval/versions`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, boolean>>
  }

  /** Upserts an app view row (by project_id + view_id). */
  async upsertProjectView(projectId: string, view: { view_id: string | number; view_name: string; all_blocks?: any; allBlocks?: any }): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/views`,
        { projectId, view },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Renames an app view. */
  async updateProjectViewName(projectId: string, viewId: string | number, viewName: string): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/views/name`,
        { projectId, viewId, viewName },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes an app view. */
  async deleteProjectView(projectId: string, viewId: string | number): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/views`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, viewId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Saves a named snapshot of all current app views as a version (max 10, oldest evicted). */
  async setVersion(projectId: string, versionName: string): Promise<{ success: boolean }> {
    const res = await this.http
      .post<{ data: { success: boolean } }>(`${environment.agentsUrl}v4/creation/versions`,
        { projectId, versionName },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return { success: res?.data?.success ?? false }
  }

  /** Restores the view snapshots of a named version back into the live app views. */
  async applyVersion(projectId: string, versionName: string): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/versions/apply`,
        { projectId, versionName },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes all rows of a named version. */
  async deleteVersion(projectId: string, versionName: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/versions`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, versionName },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
